import {
  type Account,
  Logo,
  Sidebar,
  type SidebarChild,
  type SidebarDestination,
  TopBar,
} from '@alison/ui';
import { useTenant } from '@alison/whitelabel/react';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { SHELL_CONFIG } from '../config/shellConfig.js';
import { DEFAULT_ROLE } from '../data/headerMocks.js';
import { useClients } from '../lib/filters.hooks.js';
import { mapNotification } from '../lib/notificationFeed.js';
import {
  useDismissNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationChannels,
  useNotificationFeed,
} from '../lib/notifications.hooks.js';
import { carriedSearch } from '../lib/scope-search.js';
import { useSignals } from '../lib/signals.hooks.js';
import { signalCounts } from '../lib/signals.js';
import { ClientMark, ClientMarkTooltip } from './ClientMark.js';
import { channelDelivery } from './settings/notifications/notifications.data.js';
import { SIGNALS_PANEL_WIDTH, SignalsPanel } from './signals/SignalsPanel.js';
import { useTour } from './tour/TourProvider.js';

// The rail's Signals button, so closing the panel can hand focus back to what opened it.
const SIGNALS_TRIGGER = '[data-test="nav-signals"]';
// Docking takes 400px from the page; narrower windows keep the panel floating.
const SIGNALS_PIN_MIN_WIDTH = 1280;

// channels.list calls TLMS, so the shell refreshes it far less often than the settings page.
const CHANNELS_STALE_MS = 5 * 60_000;

// The guidebook opens in its own tab so the user keeps their place in the app.
const GUIDEBOOK_PATH = '/guidebook';

const ROUTE_BY_DESTINATION = {
  dashboard: '/dashboard',
  workflows: '/workflows',
  reports: '/reports/performance',
  explore: '/explore',
  signals: '/dashboard',
  outcomes: '/outcomes',
  studio: '/studio',
  settings: '/settings/users',
} as const satisfies Record<SidebarDestination, string>;

const ROUTE_BY_CHILD = {
  creatives: '/explore',
  elements: '/elements-explorer',
  competitors: '/competition',
  performance: '/reports/performance',
  media: '/media',
} as const satisfies Record<SidebarChild, string>;

interface AppShellProps {
  account: Account;
  onLogout: () => void;
  topBar?: ReactNode;
  topBarActions?: ReactNode;
  children?: ReactNode;
  active: SidebarDestination;
  sx?: SxProps<Theme>;
}

function activeChildFor(pathname: string): SidebarChild | undefined {
  const matched = (Object.keys(ROUTE_BY_CHILD) as SidebarChild[]).filter((child) =>
    pathname.startsWith(ROUTE_BY_CHILD[child]),
  );
  return matched.sort(
    (first, second) => ROUTE_BY_CHILD[second].length - ROUTE_BY_CHILD[first].length,
  )[0];
}

export function AppShell({
  account,
  onLogout,
  topBar,
  topBarActions,
  children,
  active,
  sx,
}: AppShellProps): ReactNode {
  const navigate = useNavigate();
  const goTo = (
    to: (typeof ROUTE_BY_DESTINATION)[SidebarDestination] | (typeof ROUTE_BY_CHILD)[SidebarChild],
  ): void => {
    void navigate({
      to,
      search: (previous) => carriedSearch(pathname, to, previous as Record<string, unknown>),
    });
  };
  const tour = useTour();
  const feed = useNotificationFeed();
  const notifications = useMemo(() => (feed.data ?? []).map(mapNotification), [feed.data]);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const dismiss = useDismissNotification();
  const channelsQuery = useNotificationChannels({
    enabled: SHELL_CONFIG.notifications,
    staleTime: CHANNELS_STALE_MS,
  });
  // A failed refetch keeps the last good data; never claim delivery from it.
  const channels = channelsQuery.isError ? undefined : channelsQuery.data;
  const pathname = useLocation({ select: (location) => location.pathname });
  const activeChild = activeChildFor(pathname);
  const tenant = useTenant();
  // The rail carries the client's mark and the provider's moves to the top bar's far end.
  const client = useClients().data?.[0] ?? null;
  const startTour = tour?.canStart === true ? tour.start : null;
  const visibleNav = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(SHELL_CONFIG.nav).map(([id, on]) => [
          id,
          on && !tenant.hiddenNav.includes(id),
        ]),
      ) as Record<SidebarDestination, boolean>,
    [tenant.hiddenNav],
  );

  const signals = useSignals();
  const canPin = useMediaQuery(`(min-width:${SIGNALS_PIN_MIN_WIDTH}px)`);
  // A pin only docks; in a narrow window it waits for the room instead of floating unasked.
  const [panelOpen, setPanelOpen] = useState(false);
  const { setPinned } = signals;
  const pinned = signals.state.pinned;
  const docked = pinned && canPin;
  const showPanel = panelOpen || docked;
  const dismissPanel = useCallback((restoreFocus: boolean): void => {
    setPanelOpen(false);
    if (restoreFocus) document.querySelector<HTMLElement>(SIGNALS_TRIGGER)?.focus();
  }, []);
  const closePanel = useCallback(
    (restoreFocus: boolean): void => {
      dismissPanel(restoreFocus);
      if (pinned) setPinned(false);
    },
    [dismissPanel, pinned, setPinned],
  );
  const isTrigger = useCallback(
    (target: Node): boolean =>
      target instanceof Element && target.closest(SIGNALS_TRIGGER) !== null,
    [],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: 'background.page',
        '@media print': { display: 'block', minHeight: 0, bgcolor: 'background.paper' },
      }}
    >
      <Box sx={{ '@media print': { display: 'none' } }}>
        <Sidebar
          active={showPanel ? 'signals' : active}
          onNavigate={(destination) => {
            if (destination === 'signals') {
              if (showPanel) closePanel(false);
              else setPanelOpen(true);
              return;
            }
            goTo(ROUTE_BY_DESTINATION[destination]);
          }}
          {...(activeChild === undefined ? {} : { activeChild })}
          onNavigateChild={(child) => goTo(ROUTE_BY_CHILD[child])}
          signalCount={signalCounts(signals.signals, signals.state).new}
          visible={visibleNav}
          showAccount={SHELL_CONFIG.account}
          logoTitle={client === null ? tenant.providerName : `${client.name} · Go to Home`}
          {...(client === null
            ? tenant.isAlison
              ? {}
              : { logo: <Logo variant="glyph" src={tenant.icon} alt={tenant.providerName} /> }
            : {
                logo: <ClientMark name={client.name} logoUrl={client.logoUrl} />,
                logoTooltip: <ClientMarkTooltip name={client.name} />,
              })}
          account={{
            ...account,
            onSettings: () => void navigate({ to: '/settings/users' }),
            onLogout,
          }}
        />
      </Box>
      {showPanel ? (
        <SignalsPanel
          query={signals}
          docked={docked}
          focusOnMount={panelOpen}
          canPin={canPin}
          onClose={closePanel}
          onDismiss={dismissPanel}
          onTogglePin={() => {
            setPanelOpen(pinned);
            setPinned(!pinned);
          }}
          ignoreOutside={isTrigger}
          onOpen={(signal) => {
            signals.markRead(signal.runId);
            if (!docked) setPanelOpen(false);
          }}
        />
      ) : null}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          ml: (theme) => `${theme.layout.sidebarWidth + (docked ? SIGNALS_PANEL_WIDTH : 0)}px`,
          transition: 'margin-left .2s',
          display: 'flex',
          flexDirection: 'column',
          '@media print': {
            ml: 0,
            display: 'block',
            '& > header': { display: 'none' },
          },
        }}
      >
        <TopBar
          actions={topBarActions}
          brand={
            <Tooltip title={`Powered by ${tenant.providerName}`} placement="bottom-end">
              <Box
                data-testid="provider-mark"
                sx={{ display: 'flex', alignItems: 'center', cursor: 'default' }}
              >
                <Logo
                  variant="glyph"
                  height={20}
                  {...(tenant.isAlison ? {} : { src: tenant.icon })}
                  alt={tenant.providerName}
                />
              </Box>
            </Tooltip>
          }
          role={DEFAULT_ROLE}
          showNotifications={SHELL_CONFIG.notifications}
          onOpenGuide={() => window.open(GUIDEBOOK_PATH, '_blank', 'noopener')}
          {...(startTour === null ? {} : { onStartTour: startTour })}
          notifications={{
            items: notifications,
            onAct: (notification) => markRead.mutate({ id: Number(notification.id) }),
            onMarkAllRead: () => markAllRead.mutate(),
            onDismiss: (id) => dismiss.mutate({ id: Number(id) }),
            delivery: channelDelivery(channels),
          }}
        >
          {topBar}
        </TopBar>
        <Box component="main" sx={{ flex: 1, ...sx, '@media print': { p: 0 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
